/**
 * The section that shows a bullet / checklist of what you'll get with the course.
 */
import { ComponentChildren } from 'preact';
import { EditorProps, SalesBlockInitialState, ViewerProps } from './types';
import { useEffect, useRef } from 'preact/hooks';
import { SubsectionDragProvider } from './subsection-drag-provider';
import { SubsectionEditor } from './subsection-toolbar';
import { MultilineProp, RichProp } from './editable';
import { useIdGen } from './id-gen';
import { BtnAdd } from './btn-add';
import { LegacyContainerWrapper } from './container-wrapper';

type Bullet = {
  id: number;
  text: string;
};

type State = {
  richText: string;
  bullets: Bullet[];
};

export const name = "What you'll learn";

export const type = 'checklist';

export const initialBlockState: SalesBlockInitialState = {
  type,
  paddingt: 'md',
  bulletStyle: 'check-circle',
  bulletColor: '#10b981',
};

export const initialState: State = {
  richText: `<h2>What you'll learn</h2>`,
  bullets: [
    { id: 1, text: 'How to build a color palette' },
    { id: 2, text: 'The 5 key qualities of fine art' },
    { id: 3, text: 'How to paint landscapes' },
    { id: 4, text: 'Advanced watercolor techniques' },
    { id: 5, text: 'Ratios and layout' },
    { id: 6, text: 'How to enjoy the journey' },
  ],
};

export function MiniView() {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto grid grid-cols-3 text-xs gap-2">
        <span class="flex bg-gray-200 rounded-full h-1"></span>
        <span class="flex bg-gray-200 rounded-full h-1"></span>
        <span class="flex bg-gray-200 rounded-full h-1"></span>
        <span class="flex bg-gray-200 rounded-full h-1"></span>
        <span class="flex bg-gray-200 rounded-full h-1"></span>
        <span class="flex bg-gray-200 rounded-full h-1"></span>
      </span>
    </span>
  );
}

function CheckLI({ children }: { children: ComponentChildren }) {
  return <li>{children}</li>;
}

export function Viewer(props: ViewerProps<State>) {
  return (
    <LegacyContainerWrapper>
      <RichProp {...props} path="richText" />
      <div class="text-lg mt-6">
        <ul class="col-span-full grid @md:grid-cols-2 @lg:grid-cols-3 gap-10 text-lg pl-0">
          {props.state.bullets.map((x) => (
            <CheckLI key={x.id}>{x.text}</CheckLI>
          ))}
        </ul>
      </div>
    </LegacyContainerWrapper>
  );
}

function ChecklistEditor(props: EditorProps<State>) {
  const nextId = useIdGen(props.state.bullets);
  const ref = useRef<HTMLElement | undefined>(undefined);

  useEffect(() => {
    if (!props.isSelected || !ref.current) {
      return;
    }
    const textareas = Array.from(ref.current.querySelectorAll('textarea'));
    textareas.find((ta) => !ta.value)?.select();
  }, [props.state.bullets.length, props.isSelected]);

  return (
    <SubsectionDragProvider
      table={type}
      onReorder={(reorder) => props.setState((s) => ({ ...s, bullets: reorder(s.bullets) }))}
    >
      <div class="grid @lg:grid-cols-3 gap-10 text-lg mt-8" ref={ref as any}>
        <ul class="col-span-full grid @md:grid-cols-2 @lg:grid-cols-3 gap-10 text-lg pl-0">
          {props.state.bullets.map((b, i) => (
            <SubsectionEditor
              key={b.id}
              id={b.id}
              table={type}
              onDelete={() =>
                props.setState((s) => ({ ...s, bullets: s.bullets.filter((x) => x.id !== b.id) }))
              }
            >
              <CheckLI>
                <MultilineProp {...props} path={['bullets', i, 'text']} />
              </CheckLI>
            </SubsectionEditor>
          ))}
        </ul>
        {props.isSelected && (
          <BtnAdd
            isBgLight={props.isBgLight}
            onClick={() =>
              props.setState((s) => ({
                ...s,
                bullets: [...s.bullets, { id: nextId(), text: '' }],
              }))
            }
          >
            + Add Item
          </BtnAdd>
        )}
      </div>
    </SubsectionDragProvider>
  );
}

export function Editor(props: EditorProps<State>) {
  return (
    <LegacyContainerWrapper>
      <RichProp {...props} path="richText" />
      <ChecklistEditor {...props} />
    </LegacyContainerWrapper>
  );
}
